import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Trophy,
  Users,
  CheckCircle2,
  AlertTriangle,
  Dice5,
  RefreshCw,
  Loader2,
  Gift,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GOLD_BTN_STYLE = {
  background: "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
  color: "#1a1410",
  boxShadow:
    "0 10px 30px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.35)",
};

/**
 * PredictionDrawDialog — fetches prediction stats + game matches, and lets
 * admin/merchant either:
 *   • "draw" mode: trigger a random draw among all-correct participants
 *   • "all_winners" mode (100% gagnant): distribute the guaranteed prize to
 *     EVERY all-correct participant — claim emails are sent automatically.
 *
 * Props:
 *   open, onOpenChange
 *   game — { id, title, winners_count, sport }
 */
export default function PredictionDrawDialog({ open, onOpenChange, game }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [fullGame, setFullGame] = useState(null);
  const [winnersCount, setWinnersCount] = useState(game?.winners_count || 1);
  const [drawing, setDrawing] = useState(false);
  const [winners, setWinners] = useState([]);

  useEffect(() => {
    if (!open || !game?.id) return;
    setWinners([]);
    setStats(null);
    setFullGame(null);
    Promise.all([
      api.get(`/games/${game.id}/prediction/stats`),
      api.get(`/games/${game.id}`),
    ])
      .then(([s, g]) => {
        setStats(s.data);
        setFullGame(g.data);
        if (s.data.winners_count) setWinnersCount(s.data.winners_count);
        if (s.data.winners?.length) setWinners(s.data.winners);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [open, game?.id]);

  const sport = fullGame?.sport || game?.sport || "football";
  const matches = fullGame?.matches || [];
  const mode = stats?.reward_mode || fullGame?.prediction_reward_mode || "draw";
  const isAllWinners = mode === "all_winners";
  const prizeLabel = fullGame?.guaranteed_prize_label || "";
  const allResultsSet =
    matches.length > 0 &&
    matches.every((m) =>
      sport === "tennis"
        ? m.real_winner === "a" || m.real_winner === "b"
        : m.real_score_a != null && m.real_score_b != null
    );

  async function refreshStats() {
    const s = await api.get(`/games/${game.id}/prediction/stats`);
    setStats(s.data);
    return s.data;
  }

  async function runDraw() {
    if (drawing || !allResultsSet) return;
    setDrawing(true);
    try {
      const { data } = await api.post(`/games/${game.id}/prediction/draw`, {
        winners_count: winnersCount,
      });
      await new Promise((res) => setTimeout(res, 900));
      setWinners(data.winners || []);
      await refreshStats();
      if (data.winners?.length) {
        toast.success(t("lottery.draw_success"));
      } else {
        toast.info(t("game_editor.pred_missing_results"));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  }

  async function runDistribute() {
    if (drawing || !allResultsSet) return;
    setDrawing(true);
    try {
      const { data } = await api.post(
        `/games/${game.id}/prediction/distribute`
      );
      await refreshStats();
      if (data.eligible_count === 0) {
        toast.info(t("lottery.distribute_none"));
      } else {
        toast.success(
          t("lottery.distribute_success", {
            count: data.newly_rewarded + data.already_rewarded,
            emails: data.emails_sent,
          })
        );
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDrawing(false);
    }
  }

  const hasPrev = !!stats?.lottery_drawn_at;
  const alreadyDrew = hasPrev && winners.length > 0;
  const rewarded = stats?.rewarded || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="prediction-draw-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isAllWinners ? (
              <Gift className="h-5 w-5" style={{ color: "#d4af37" }} />
            ) : (
              <Trophy className="h-5 w-5" style={{ color: "#d4af37" }} />
            )}
            {isAllWinners
              ? t("lottery.distribute_now")
              : t("game_editor.pred_draw_btn")}
          </DialogTitle>
          <DialogDescription>{game?.title}</DialogDescription>
        </DialogHeader>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 py-2">
          <div className="qrise-card p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              <Users className="h-3 w-3" />
              {t("lottery.total_participants")}
            </div>
            <div
              className="mt-1 text-3xl font-semibold tracking-tight"
              data-testid="pred-total-participants"
            >
              {stats?.total_participants ?? "—"}
            </div>
          </div>
          <div className="qrise-card p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3" />
              All-correct
            </div>
            <div
              className="mt-1 text-3xl font-semibold tracking-tight text-emerald-600 dark:text-emerald-400"
              data-testid="pred-eligible-count"
            >
              {stats?.eligible_count ?? "—"}
            </div>
          </div>
          <div className="qrise-card p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
              {isAllWinners ? (
                <Gift className="h-3 w-3" />
              ) : (
                <Trophy className="h-3 w-3" />
              )}
              {isAllWinners
                ? t("lottery.rewarded_label")
                : t("lottery.configured_winners")}
            </div>
            <div
              className="mt-1 text-3xl font-semibold tracking-tight"
              data-testid={
                isAllWinners ? "pred-rewarded-count" : "pred-winners-config"
              }
            >
              {isAllWinners
                ? (stats?.rewarded_count ?? "—")
                : (stats?.winners_count ?? game?.winners_count ?? 1)}
            </div>
          </div>
        </div>

        {/* Matches summary */}
        {matches.length > 0 && (
          <div className="rounded-xl border border-border p-3 space-y-2 max-h-56 overflow-auto">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">
              {t("game_editor.pred_matches_title")}
            </div>
            {matches.map((m) => {
              const a = m.team_a || {};
              const b = m.team_b || {};
              const done =
                sport === "tennis"
                  ? m.real_winner === "a" || m.real_winner === "b"
                  : m.real_score_a != null && m.real_score_b != null;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-2 text-xs"
                  data-testid={`pred-draw-match-${m.id}`}
                >
                  <span className="flex-1 truncate">
                    {a.name} vs {b.name}
                  </span>
                  {done ? (
                    <span className="font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                      {sport === "tennis"
                        ? m.real_winner === "a"
                          ? a.name
                          : b.name
                        : `${m.real_score_a} - ${m.real_score_b}`}
                    </span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px]">
                      pending
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Missing-results warning */}
        {!allResultsSet && matches.length > 0 && (
          <div className="rounded-xl border border-amber-400/40 bg-amber-400/10 text-amber-700 dark:text-amber-300 p-3 text-xs inline-flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{t("game_editor.pred_missing_results")}</span>
          </div>
        )}

        {isAllWinners ? (
          <>
            {/* Guaranteed prize summary + distribute button */}
            <div
              className="rounded-xl border border-border p-4 flex flex-wrap items-center gap-3"
              data-testid="pred-distribute-block"
            >
              <div className="flex-1 min-w-[180px]">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5">
                  <Gift className="h-3.5 w-3.5" />
                  {t("lottery.guaranteed_prize")}
                </div>
                <div
                  className="mt-1 text-sm font-semibold"
                  data-testid="pred-guaranteed-prize-label"
                >
                  {prizeLabel || (
                    <span className="text-rose-500 font-medium">
                      {t("game_editor.pred_prize_required")}
                    </span>
                  )}
                </div>
                {fullGame?.guaranteed_claim_deadline && (
                  <div
                    className="mt-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300"
                    data-testid="pred-claim-until"
                  >
                    {t("lottery.claim_until", {
                      date: new Date(
                        `${fullGame.guaranteed_claim_deadline}T00:00:00`
                      ).toLocaleDateString(),
                    })}
                  </div>
                )}
                <p className="mt-1 text-[11px] text-muted-foreground leading-snug">
                  {t("lottery.emails_auto_note")}
                </p>
              </div>
              <Button
                type="button"
                onClick={runDistribute}
                disabled={drawing || !allResultsSet || !prizeLabel}
                className="h-10 rounded-xl text-xs font-bold tracking-[0.2em] uppercase"
                style={GOLD_BTN_STYLE}
                data-testid="pred-run-distribute-btn"
              >
                {drawing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    {t("lottery.distributing")}
                  </>
                ) : (
                  <>
                    <Gift className="h-4 w-4 mr-1.5" />
                    {t("lottery.distribute_now")}
                  </>
                )}
              </Button>
            </div>

            {/* Rewarded list */}
            {rewarded.length > 0 && (
              <div
                className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-4"
                data-testid="pred-rewarded-list"
              >
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5 mb-3">
                  <Trophy className="h-3.5 w-3.5" />
                  {t("lottery.winners_title", { count: rewarded.length })}
                </div>
                <ul className="space-y-2 max-h-56 overflow-auto pr-1">
                  {rewarded.map((w, i) => (
                    <li
                      key={w.session_id}
                      className="flex items-center gap-3 rounded-lg bg-card border border-border px-3 py-2"
                      data-testid={`pred-rewarded-${i}`}
                    >
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                        style={{
                          background:
                            "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)",
                          color: "#1a1410",
                        }}
                      >
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {w.first_name} {w.last_name}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {w.email} · {w.phone}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${
                          w.email_sent
                            ? "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10"
                            : "text-muted-foreground bg-muted"
                        }`}
                        data-testid={`pred-rewarded-email-${i}`}
                      >
                        <Mail className="h-3 w-3" />
                        {w.email_sent
                          ? t("lottery.email_sent")
                          : t("lottery.email_pending")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No eligible hint after distribution */}
            {stats?.prizes_distributed_at && rewarded.length === 0 && (
              <div className="rounded-xl border border-border bg-muted/20 p-3 text-xs text-center text-muted-foreground">
                {t("lottery.distribute_none")}
              </div>
            )}

            {/* Previous-distribution note */}
            {stats?.prizes_distributed_at && !drawing && (
              <div className="text-[11px] text-muted-foreground text-center">
                {t("lottery.distributed_at", {
                  date: new Date(
                    stats.prizes_distributed_at
                  ).toLocaleString(),
                })}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Winners count input + draw button */}
            <div className="rounded-xl border border-border p-4 flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs font-medium">
                  {t("lottery.winners_to_draw")}
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={stats?.eligible_count || 100}
                  value={winnersCount}
                  onChange={(e) =>
                    setWinnersCount(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="pred-winners-input"
                />
              </div>
              <Button
                type="button"
                onClick={runDraw}
                disabled={drawing || !allResultsSet}
                className="h-10 rounded-xl text-xs font-bold tracking-[0.2em] uppercase"
                style={GOLD_BTN_STYLE}
                data-testid="pred-run-draw-btn"
              >
                {drawing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    {t("lottery.drawing")}
                  </>
                ) : alreadyDrew ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1.5" />
                    {t("lottery.redraw")}
                  </>
                ) : (
                  <>
                    <Dice5 className="h-4 w-4 mr-1.5" />
                    {t("lottery.draw_now")}
                  </>
                )}
              </Button>
            </div>

            {/* Winners list */}
            {winners.length > 0 && (
              <div
                className="rounded-xl border border-amber-400/40 bg-amber-400/10 p-4"
                data-testid="pred-winners-list"
              >
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1.5 mb-3">
                  <Trophy className="h-3.5 w-3.5" />
                  {t("lottery.winners_title", { count: winners.length })}
                </div>
                <ul className="space-y-2">
                  {winners.map((w, i) => (
                    <li
                      key={w.session_id}
                      className="flex items-center gap-3 rounded-lg bg-card border border-border px-3 py-2"
                      data-testid={`pred-winner-${i}`}
                    >
                      <div
                        className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{
                          background:
                            "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)",
                          color: "#1a1410",
                        }}
                      >
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {w.first_name} {w.last_name}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {w.email} · {w.phone}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No eligible hint after draw */}
            {hasPrev && winners.length === 0 && (
              <div className="rounded-xl border border-border bg-muted/20 p-3 text-xs text-center text-muted-foreground">
                {t("game_editor.pred_missing_results")} — 0 all-correct.
              </div>
            )}

            {/* Previous-draw note */}
            {hasPrev && !drawing && (
              <div className="text-[11px] text-muted-foreground text-center">
                {t("lottery.drawn_at", {
                  date: new Date(stats.lottery_drawn_at).toLocaleString(),
                })}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
